import { Button, InputNumber, InputCurrency } from "@sellar-io/components";
import { SupplierRelationshipDiscountCreateRequestType } from "@sellar-io/api-v2/types";
import { useForm, useWatch } from "react-hook-form";
import { Alert } from "../../../components/alert/Alert";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useSupplierRelationshipDiscountCreate } from "../../../hooks/api/v2/supplierRelationshipDiscount/useSupplierRelationshipDiscountCreate";
import { formatPriceInputToApi } from "../../../hooks/usePriceFormat";
import { useEffect } from "react";

export const BusinessConnectionDiscountAddDialog = ({
  active,
  onDismiss,
  connectionId,
}: {
  active: boolean;
  onDismiss: () => void;
  connectionId: number;
}) => {
  const { reset, ...formHook } =
    useForm<SupplierRelationshipDiscountCreateRequestType>({
      shouldUseNativeValidation: true,
    });
  useEffect(() => {
    if (active) {
      reset({
        type: "flat",
        itemType: "any",
        discountAmount: undefined,
      });
    }
  }, [active, reset]);

  const { type } = useWatch({
    control: formHook.control,
  });

  const connectionDiscountCreateMutation =
    useSupplierRelationshipDiscountCreate();

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={"Add discount"}
        description={
          "Discounts will be applied to the product item prices of the price tier you have assigned to this customer."
        }
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <form
        onSubmit={formHook.handleSubmit((data) =>
          connectionDiscountCreateMutation.mutate(
            {
              type: data.type,
              itemType: data.itemType,
              SupplierRetailerConnectionId: connectionId,
              discountAmount:
                data.type === "flat"
                  ? (formatPriceInputToApi(data.discountAmount) ?? 0)
                  : data.discountAmount,
            },
            {
              onSuccess: () => onDismiss(),
            },
          ),
        )}
      >
        <DialogContent>
          {connectionDiscountCreateMutation.isError ? (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(connectionDiscountCreateMutation.error)
                  ?.response?.data.message ?? "Unknown error occurred."
              }
            />
          ) : null}

          <InputLabel>Discount type</InputLabel>
          <InputContainer>
            <InputSelect {...formHook.register("type", { required: true })}>
              <InputOption value={"flat"}>Flat</InputOption>
              <InputOption value={"percentage"}>Percentage</InputOption>
            </InputSelect>
          </InputContainer>

          <InputLabel>
            Discount amount ({type === "flat" ? "£" : "%"})
          </InputLabel>
          <InputContainer>
            {type === "flat" ? (
              <InputCurrency
                showPrefix
                allowNegative
                {...formHook.register("discountAmount", {
                  required: true,
                  min: 0,
                  valueAsNumber: true,
                })}
              />
            ) : (
              <InputNumber
                {...formHook.register("discountAmount", {
                  required: true,
                  min: 0,
                  valueAsNumber: true,
                })}
                step={"0.01"}
              />
            )}
          </InputContainer>
          <InputLabel>Container type</InputLabel>
          <InputContainer>
            <InputSelect {...formHook.register("itemType", { required: true })}>
              <InputOption value={"any"}>Any</InputOption>
              <InputOption value={"bottle"}>Bottle</InputOption>
              <InputOption value={"can"}>Can</InputOption>
              <InputOption value={"cask"}>Cask</InputOption>
              <InputOption value={"keg"}>Keg</InputOption>
            </InputSelect>
          </InputContainer>
        </DialogContent>
        <DialogCTA>
          <Button
            loading={connectionDiscountCreateMutation.isPending}
            type="submit"
          >
            Add
          </Button>
        </DialogCTA>
      </form>
    </Dialog>
  );
};
